'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { BadgeCheck } from 'lucide-react'
import { type Tournament, gameIcon } from '@/lib/tournaments'
import { StatusBadge } from './shared'
import { UserAvatar } from '@/components/ui/user-avatar'

/**
 * Full-bleed immersive tournament hero (cover/accent backdrop, status + game
 * chips, title, organizers). Shared by the detail page and the results page so
 * both share an identical masthead.
 *
 * `pb` controls the bottom padding: detail uses the default so its floating
 * stat bar can overlap; pass a smaller value when nothing overlaps below.
 */
export function TournamentHero({
  t,
  locale,
  pb = 'pb-44',
}: {
  t: Tournament
  locale: string
  pb?: string
}) {
  return (
    <div className="relative overflow-hidden">
      {t.coverImage ? (
        <>
          <Image
            src={t.coverImage}
            alt=""
            fill
            priority
            sizes="100vw"
            aria-hidden
            className="object-cover object-center"
          />
          {/* keep the left-side title legible; fade into the page at the bottom */}
          <div className="absolute inset-0 bg-gradient-to-r from-background/85 via-background/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${t.accent.from}, ${t.accent.to})`,
            }}
          />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
          {/* oversized ghost icon */}
          <Image
            src={gameIcon(t.game)}
            alt=""
            width={420}
            height={420}
            unoptimized
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-10 size-[420px] rotate-12 opacity-20 blur-[1px]"
          />
        </>
      )}

      <div className={`relative mx-auto max-w-5xl px-4 pt-32 ${pb}`}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={t.status} className="bg-black/40 backdrop-blur" />
            <Link
              href={`/${locale}/tournaments?game=${t.gameSlug}`}
              className="flex items-center gap-1.5 rounded-full bg-black/40 px-2.5 py-0.5 text-xs text-white/90 backdrop-blur transition-opacity hover:opacity-80"
            >
              <Image src={gameIcon(t.game)} alt="" width={14} height={14} unoptimized className="rounded" />
              {t.game}
            </Link>
          </div>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
            {t.title}
          </h1>
          {t.organizers.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-white/80">
              Organized by
              <div className="flex items-center -space-x-2">
                {t.organizers.map((o) => (
                  <UserAvatar
                    key={o.id}
                    url={o.avatar}
                    alt={o.gamertag}
                    size={24}
                    className="ring-2 ring-black/40"
                  />
                ))}
              </div>
              <span className="font-semibold text-white">
                {t.organizers.map((o, i) => (
                  <span key={o.id}>
                    {i > 0 && ', '}
                    <Link
                      href={`/${locale}/profile/${encodeURIComponent(o.gamertag)}`}
                      className="underline-offset-2 transition-colors hover:text-white/80 hover:underline"
                    >
                      {o.gamertag}
                    </Link>
                  </span>
                ))}
              </span>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-sm text-white/80">
              Organized by
              {/* Imported events have no organizer *users*, but their organizer
                  may have a profile at /org/<slug>. Link when it resolved;
                  otherwise the name stays plain text. */}
              {t.organizationSlug ? (
                <Link
                  href={`/${locale}/org/${encodeURIComponent(t.organizationSlug)}`}
                  className="font-semibold text-white underline-offset-2 transition-colors hover:text-white/80 hover:underline"
                >
                  {t.organizer.name}
                </Link>
              ) : (
                <span className="font-semibold text-white">{t.organizer.name}</span>
              )}
              {t.organizer.verified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/20 px-2 py-0.5 text-xs text-sky-200">
                  <BadgeCheck className="size-3.5" /> Verified
                </span>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
